import {
  canReadProperties,
  canWriteProperties,
  type CurrentAuthorization,
} from '@/api/core/authorization';
import type { CatalogRoom, CatalogSlot, PropertyCatalog } from '@/api/core/properties';
import {
  readUnitSaleClassification,
  setUnitSaleClassification,
  UNIT_SALE_CLASSIFICATIONS,
  type UnitSaleClassification,
  type UnitSaleClassificationCommand,
  type UnitSaleClassificationReceipt,
  type UnitSaleClassificationView,
} from '@/api/core/unit-sale-classification';
import { isApiError } from '@/api/http/errors';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useEffect, useRef, useState } from 'react';
import type { PropertyRuntime } from '../property-queries';

const label: Record<UnitSaleClassification, string> = {
  GENERAL: '일반',
  MIDTERM: '중기',
  STAY: '숙박',
  STAFF: '직원',
  SHOWROOM: '쇼룸',
  SHORT_TERM: '단기',
  FEMALE_ONLY: '여성 전용',
  MALE_ONLY: '남성 전용',
};
const canResolveUnknown = (error: unknown) =>
  !isApiError(error) ||
  error.kind === 'network' ||
  error.kind === 'timeout' ||
  (error.kind === 'http' && (error.status ?? 0) >= 500);

interface Props {
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  room: Pick<CatalogRoom, 'id' | 'topologyRevision'>;
  slot: Pick<CatalogSlot, 'id' | 'roomId'>;
  page: {
    property: Pick<PropertyCatalog['property'], 'property_id'> & { branch_id: number | null };
    rooms: Pick<CatalogRoom, 'id'>[];
    slots: Pick<CatalogSlot, 'id' | 'roomId'>[];
  };
  authorization: CurrentAuthorization | null;
  isCurrent: () => boolean;
  ownerBoundary?: object;
  mode?: 'native-initialization';
}

interface CommandOwnerState {
  intent: UnitSaleClassificationCommand | null;
  receipt: UnitSaleClassificationReceipt | null;
  unknown: boolean;
}
const emptyCommandOwnerState: CommandOwnerState = { intent: null, receipt: null, unknown: false };
const commandOwnerByRuntime = new WeakMap<object, Map<string, CommandOwnerState>>();
const activeCommandOwnerByRuntime = new WeakMap<object, string>();
interface ActiveBoundaryOwner {
  runtime: object;
  key: string;
}
const activeCommandOwnerByBoundary = new WeakMap<object, ActiveBoundaryOwner>();
const runtimeIds = new WeakMap<object, number>();
let nextRuntimeId = 1;
const sessionIds = new WeakMap<object, number>();
let nextSessionId = 1;

interface UnitClassificationOwnerScope {
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  branchId: number | null;
  authorization: CurrentAuthorization | null;
  sessionStatus: string;
  propertyDenied: boolean;
  enabled: boolean;
}

interface TrackedParentOwner {
  runtime: PropertyRuntime;
  scopeKey: string;
}

const parentOwnerByBoundary = new WeakMap<object, TrackedParentOwner>();

function ownerScopeKey({
  runtime,
  generation,
  propertyId,
  branchId,
  authorization,
  sessionStatus,
  propertyDenied,
  enabled,
}: UnitClassificationOwnerScope) {
  let runtimeId = runtimeIds.get(runtime);
  if (runtimeId === undefined) {
    runtimeId = nextRuntimeId++;
    runtimeIds.set(runtime, runtimeId);
  }
  let sessionId = sessionIds.get(runtime.session);
  if (sessionId === undefined) {
    sessionId = nextSessionId++;
    sessionIds.set(runtime.session, sessionId);
  }
  return JSON.stringify([
    runtimeId,
    sessionId,
    runtime.apiNamespace,
    propertyId.toLowerCase(),
    branchId,
    generation,
    JSON.stringify(authorization),
    sessionStatus,
    propertyDenied,
    enabled,
  ]);
}

function ownerKeyScope(key: string) {
  try {
    const parsed: unknown = JSON.parse(key);
    return Array.isArray(parsed) && typeof parsed[0] === 'string' ? parsed[0] : null;
  } catch {
    return null;
  }
}

function expireOwnerScope(boundary: object, { runtime, scopeKey }: TrackedParentOwner) {
  const owners = commandOwnerByRuntime.get(runtime);
  if (owners) {
    for (const key of owners.keys()) {
      if (ownerKeyScope(key) === scopeKey) owners.delete(key);
    }
  }
  if (ownerKeyScope(activeCommandOwnerByRuntime.get(runtime) ?? '') === scopeKey)
    activeCommandOwnerByRuntime.delete(runtime);
  const activeBoundaryOwner = activeCommandOwnerByBoundary.get(boundary);
  if (
    activeBoundaryOwner?.runtime === runtime &&
    ownerKeyScope(activeBoundaryOwner.key) === scopeKey
  )
    activeCommandOwnerByBoundary.delete(boundary);
}

/** Records committed property ownership while the Unit dialog may be absent. */
export function observeUnitClassificationParentOwner(
  boundary: object,
  owner: UnitClassificationOwnerScope,
) {
  const next = { runtime: owner.runtime, scopeKey: ownerScopeKey(owner) };
  const previous = parentOwnerByBoundary.get(boundary);
  if (previous && (previous.runtime !== next.runtime || previous.scopeKey !== next.scopeKey))
    expireOwnerScope(boundary, previous);
  parentOwnerByBoundary.set(boundary, next);
}

export function UnitSaleClassificationSection({
  runtime,
  generation,
  propertyId,
  room,
  slot,
  page,
  authorization,
  isCurrent,
  ownerBoundary,
  mode,
}: Props) {
  const [current, setCurrent] = useState<UnitSaleClassificationView | null>(null);
  const [selection, setSelection] = useState<UnitSaleClassification | null>(null);
  const localOwnerBoundary = useRef<object>({});
  const stableOwnerBoundary = ownerBoundary ?? localOwnerBoundary.current;
  const snapshot = runtime.session.getSnapshot();
  // Native initialization uses the confirmed single-Unit contract; legacy rollout stays independent.
  const enabled = mode === 'native-initialization' || runtime.unitClassificationEnabled === true;
  const ownerScope = {
    runtime,
    generation,
    propertyId,
    branchId: page.property.branch_id,
    authorization,
    sessionStatus: snapshot.status,
    propertyDenied: snapshot.deniedDomains.includes('property'),
    enabled,
  };
  const parentScopeKey = ownerScopeKey(ownerScope);
  const ownerKey = JSON.stringify([parentScopeKey, slot.id.toLowerCase(), mode ?? 'legacy']);
  let sessionOwners = commandOwnerByRuntime.get(runtime);
  if (!sessionOwners) {
    sessionOwners = new Map();
    commandOwnerByRuntime.set(runtime, sessionOwners);
  }
  const [ownerSnapshot, setOwnerSnapshot] = useState(() => ({
    key: ownerKey,
    state: sessionOwners.get(ownerKey) ?? emptyCommandOwnerState,
  }));
  const activeOwnerKey = activeCommandOwnerByRuntime.get(runtime);
  const activeBoundaryOwner = activeCommandOwnerByBoundary.get(stableOwnerBoundary);
  const ownerState =
    (activeOwnerKey !== undefined && activeOwnerKey !== ownerKey) ||
    (activeBoundaryOwner !== undefined &&
      (activeBoundaryOwner.runtime !== runtime || activeBoundaryOwner.key !== ownerKey))
      ? emptyCommandOwnerState
      : ownerSnapshot.key === ownerKey
        ? ownerSnapshot.state
        : (sessionOwners.get(ownerKey) ?? emptyCommandOwnerState);
  const { intent, receipt, unknown } = ownerState;
  const updateOwner = (patch: Partial<CommandOwnerState>) => {
    setOwnerSnapshot((previous) => {
      const previousState =
        previous.key === ownerKey
          ? previous.state
          : activeCommandOwnerByRuntime.get(runtime) === ownerKey
            ? (sessionOwners.get(ownerKey) ?? emptyCommandOwnerState)
            : emptyCommandOwnerState;
      const next = { ...previousState, ...patch };
      sessionOwners.set(ownerKey, next);
      return { key: ownerKey, state: next };
    });
  };
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const activeScope = useRef(0);
  const scopeEpoch = useRef(0);
  const ownerKeyRef = useRef(ownerKey);
  ownerKeyRef.current = ownerKey;
  const authorized =
    authorization !== null &&
    canReadProperties(authorization) &&
    canWriteProperties(authorization) &&
    snapshot.authorization !== null &&
    JSON.stringify(snapshot.authorization) === JSON.stringify(authorization) &&
    !snapshot.deniedDomains.includes('property');
  const targetIsCurrent =
    page.property.property_id.toLowerCase() === propertyId.toLowerCase() &&
    page.rooms.some((item) => item.id.toLowerCase() === room.id.toLowerCase()) &&
    slot.roomId.toLowerCase() === room.id.toLowerCase() &&
    page.slots.some(
      (item) =>
        item.id.toLowerCase() === slot.id.toLowerCase() &&
        item.roomId.toLowerCase() === room.id.toLowerCase(),
    ) &&
    room.topologyRevision !== null &&
    Number.isSafeInteger(room.topologyRevision) &&
    room.topologyRevision > 0;
  const accessIsCurrent = (expectedEpoch = activeScope.current, expectedOwnerKey = ownerKey) => {
    const next = runtime.session.getSnapshot();
    return (
      enabled &&
      expectedEpoch !== 0 &&
      activeScope.current === expectedEpoch &&
      ownerKeyRef.current === expectedOwnerKey &&
      targetIsCurrent &&
      isCurrent() &&
      next.status === 'authorized' &&
      next.generation === generation &&
      next.authorization !== null &&
      JSON.stringify(next.authorization) === JSON.stringify(authorization) &&
      !next.deniedDomains.includes('property') &&
      canReadProperties(next.authorization) &&
      canWriteProperties(next.authorization)
    );
  };

  useEffect(() => {
    const epoch = ++scopeEpoch.current;
    activeScope.current = epoch;
    const previousOwnerKey = activeCommandOwnerByRuntime.get(runtime);
    const ownerChanged = previousOwnerKey !== undefined && previousOwnerKey !== ownerKey;
    const previousBoundaryOwner = activeCommandOwnerByBoundary.get(stableOwnerBoundary);
    const boundaryChanged =
      previousBoundaryOwner !== undefined &&
      (previousBoundaryOwner.runtime !== runtime || previousBoundaryOwner.key !== ownerKey);
    if (boundaryChanged) {
      const previousRuntimeOwners = commandOwnerByRuntime.get(previousBoundaryOwner.runtime);
      previousRuntimeOwners?.delete(previousBoundaryOwner.key);
      sessionOwners.delete(ownerKey);
    }
    if (ownerChanged) {
      sessionOwners.delete(previousOwnerKey);
      sessionOwners.delete(ownerKey);
    }
    activeCommandOwnerByRuntime.set(runtime, ownerKey);
    activeCommandOwnerByBoundary.set(stableOwnerBoundary, { runtime, key: ownerKey });
    if (ownerChanged) setOwnerSnapshot({ key: ownerKey, state: emptyCommandOwnerState });
    else if (ownerSnapshot.key !== ownerKey)
      setOwnerSnapshot({
        key: ownerKey,
        state: sessionOwners.get(ownerKey) ?? emptyCommandOwnerState,
      });
    setCurrent(null);
    setSelection(null);
    setMessage(null);
    return () => {
      if (activeScope.current === epoch) activeScope.current = 0;
    };
    // The full command owner scope is intentional: changing any part invalidates local view state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerKey, runtime, stableOwnerBoundary]);

  useEffect(() => {
    if (
      !enabled ||
      !authorized ||
      !targetIsCurrent ||
      snapshot.status !== 'authorized' ||
      snapshot.generation !== generation
    )
      return;
    const controller = new AbortController();
    let live = true;
    const epoch = activeScope.current;
    const requestOwnerKey = ownerKey;
    setBusy(true);
    void readUnitSaleClassification(runtime.client, slot.id, controller.signal)
      .then((value) => {
        if (live && accessIsCurrent(epoch, requestOwnerKey)) {
          setCurrent(value);
          setSelection(value.value);
          setMessage(null);
        }
      })
      .catch((error: unknown) => {
        if (!live || controller.signal.aborted || !accessIsCurrent(epoch, requestOwnerKey)) return;
        if (isApiError(error) && error.status === 403)
          runtime.session.reportDomainAccessDenied('property', generation);
        setCurrent(null);
        setMessage(errorMessage(error));
      })
      .finally(() => {
        if (live && accessIsCurrent(epoch, requestOwnerKey)) setBusy(false);
      });
    return () => {
      live = false;
      controller.abort();
    };
    // Each selected Unit is keyed by its actual current Core UUID.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    authorized,
    targetIsCurrent,
    generation,
    propertyId,
    authorization,
    room.id,
    room.topologyRevision,
    slot.id,
    runtime,
    ownerKey,
  ]);

  const refresh = async () => {
    const epoch = activeScope.current;
    const requestOwnerKey = ownerKey;
    if (!accessIsCurrent(epoch, requestOwnerKey)) return false;
    setBusy(true);
    try {
      const fresh = await readUnitSaleClassification(runtime.client, slot.id);
      if (!accessIsCurrent(epoch, requestOwnerKey)) return false;
      setCurrent(fresh);
      setSelection(fresh.value);
      updateOwner({ intent: null, unknown: false });
      setMessage(null);
      return true;
    } catch (error) {
      if (!accessIsCurrent(epoch, requestOwnerKey)) return false;
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('property', generation);
      setMessage(
        isApiError(error) && error.status === 503
          ? '현재 Unit 분류를 다시 조회하지 못했습니다. 잠시 후 최신값을 다시 조회해 주세요.'
          : errorMessage(error),
      );
      return false;
    } finally {
      if (accessIsCurrent(epoch, requestOwnerKey)) setBusy(false);
    }
  };

  const submit = async (command: UnitSaleClassificationCommand) => {
    const epoch = activeScope.current;
    const requestOwnerKey = ownerKey;
    if (!accessIsCurrent(epoch, requestOwnerKey)) return;
    if (mode === 'native-initialization' && current?.value !== null) return;
    setBusy(true);
    try {
      const result = await setUnitSaleClassification(runtime.client, slot.id, command);
      if (!accessIsCurrent(epoch, requestOwnerKey)) return;
      updateOwner({ receipt: result, unknown: false });
      setMessage(null);
      updateOwner({ intent: null });
      // The immutable receipt remains distinct from current state; fetch current state separately.
      try {
        const fresh = await readUnitSaleClassification(runtime.client, slot.id);
        if (accessIsCurrent(epoch, requestOwnerKey)) setCurrent(fresh);
      } catch (error) {
        if (accessIsCurrent(epoch, requestOwnerKey))
          setMessage(
            `명령 결과는 ${result.outcome}입니다. 현재 상태는 다시 조회해 주세요. ${errorMessage(error)}`,
          );
      }
    } catch (error) {
      if (!accessIsCurrent(epoch, requestOwnerKey)) return;
      if (isApiError(error) && error.status === 403) {
        runtime.session.reportDomainAccessDenied('property', generation);
        setCurrent(null);
        updateOwner({ receipt: null });
      } else if (isApiError(error) && error.status === 409) {
        updateOwner({ intent: null, unknown: false });
        setCurrent(null);
        setSelection(null);
        if (await refresh())
          setMessage(
            '최신 Unit 분류를 다시 읽었습니다. 새 의도로 검토해 주세요. 이전 명령은 재사용하지 않습니다.',
          );
      } else if (canResolveUnknown(error)) {
        updateOwner({ unknown: true });
        setMessage('저장 결과를 확인하지 못했습니다. 같은 명령을 그대로 다시 보낼 수 있습니다.');
      } else setMessage(errorMessage(error));
    } finally {
      if (accessIsCurrent(epoch, requestOwnerKey)) setBusy(false);
    }
  };

  if (
    !enabled ||
    !authorized ||
    !targetIsCurrent ||
    snapshot.status !== 'authorized' ||
    snapshot.generation !== generation ||
    snapshot.deniedDomains.includes('property')
  )
    return null;
  return (
    <section
      aria-label="Unit 개별 판매 분류"
      className="border-border body-sm mt-4 flex flex-col gap-4 border-t pt-4 leading-relaxed"
    >
      <div>
        <h4 className="title-sm">개별 판매 분류</h4>
        <p className="caption text-muted-foreground">
          {mode === 'native-initialization'
            ? '분류가 미설정인 이 슬롯 한 개만 초기 등록합니다. 분류 미설정은 일반으로 간주하지 않습니다.'
            : '이 Unit 한 개의 분류만 변경합니다. 분류 미설정은 일반으로 간주하지 않습니다.'}
        </p>
      </div>
      {busy && current === null ? (
        <p role="status">현재 분류를 확인하고 있습니다.</p>
      ) : (
        current && (
          <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <dt className="caption text-muted-foreground">현재 분류</dt>
              <dd className="body-strong">{current.value ? label[current.value] : '미설정'}</dd>
            </div>
            {mode !== 'native-initialization' && (
              <div>
                <dt className="caption text-muted-foreground">현재 revision</dt>
                <dd className="body-strong">{current.revision}</dd>
              </div>
            )}
          </dl>
        )
      )}
      {current &&
        (mode !== 'native-initialization' || current.value === null) &&
        !intent &&
        !unknown && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <Select
              value={selection ?? ''}
              items={UNIT_SALE_CLASSIFICATIONS.map((value) => ({ value, label: label[value] }))}
              onValueChange={(value) => {
                setSelection(UNIT_SALE_CLASSIFICATIONS.find((item) => item === value) ?? null);
              }}
            >
              <SelectTrigger aria-label="새 판매 분류" className="min-w-0 flex-1">
                <SelectValue placeholder="분류 선택" />
              </SelectTrigger>
              <SelectContent>
                {UNIT_SALE_CLASSIFICATIONS.map((value) => (
                  <SelectItem key={value} value={value}>
                    {label[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              className="h-auto min-h-8 whitespace-normal"
              disabled={
                busy ||
                selection === null ||
                selection === current.value ||
                current.room_topology_revision !== room.topologyRevision
              }
              onClick={() => {
                if (selection === null) return;
                updateOwner({
                  receipt: null,
                  intent: {
                    command_id: crypto.randomUUID(),
                    expected_revision: current.revision,
                    expected_fingerprint: current.fingerprint,
                    room_topology_revision: current.room_topology_revision,
                    classification: selection,
                  },
                });
              }}
            >
              검토
            </Button>
          </div>
        )}
      {current && current.room_topology_revision !== room.topologyRevision && (
        <p role="alert">슬롯의 현재 객실 구성이 목록과 다릅니다. 현재값을 다시 확인해 주세요.</p>
      )}
      {intent && !unknown && (
        <div
          role="group"
          aria-label="분류 변경 검토"
          className="border-border flex flex-col gap-3 rounded-md border p-4"
        >
          <p>
            {mode === 'native-initialization' ? '선택한 슬롯' : `Unit ${slot.id}`}의 분류를{' '}
            {label[intent.classification]}(으)로{' '}
            {mode === 'native-initialization' ? '초기 등록합니다.' : '변경합니다.'}
          </p>
          {mode !== 'native-initialization' && (
            <p className="caption text-muted-foreground">
              revision {intent.expected_revision} · Room 구성 revision{' '}
              {intent.room_topology_revision}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              className="h-auto min-h-8 whitespace-normal"
              disabled={busy}
              onClick={() => {
                void submit(intent);
              }}
            >
              확인하고 저장
            </Button>
            <Button
              className="h-auto min-h-8 whitespace-normal"
              variant="outline"
              disabled={busy}
              onClick={() => {
                updateOwner({ intent: null });
              }}
            >
              취소
            </Button>
          </div>
        </div>
      )}
      {unknown && intent && (
        <div
          role="group"
          aria-label="확인되지 않은 명령"
          className="flex flex-wrap items-center gap-2"
        >
          <Button
            className="h-auto min-h-8 whitespace-normal"
            disabled={busy}
            onClick={() => {
              void submit(intent);
            }}
          >
            같은 명령 재시도
          </Button>
          <Button
            className="h-auto min-h-8 whitespace-normal"
            variant="outline"
            disabled={busy}
            onClick={() => {
              void refresh();
            }}
          >
            현재값 다시 확인
          </Button>
        </div>
      )}
      {receipt && (
        <div role="status" aria-label="명령 결과">
          <p>
            {receipt.outcome === 'CHANGED'
              ? '분류를 저장했습니다.'
              : '이미 같은 분류였습니다. 저장값은 바꾸지 않았습니다.'}
          </p>
          {mode !== 'native-initialization' && (
            <p className="caption text-muted-foreground">
              최초 결과: {label[receipt.value]} · revision {receipt.result_revision}
            </p>
          )}
        </div>
      )}
      {message && <p role="alert">{message}</p>}
      {current === null && !busy && (
        <Button
          className="h-auto min-h-8 whitespace-normal"
          variant="outline"
          disabled={busy}
          onClick={() => {
            void refresh();
          }}
        >
          최신값 다시 조회
        </Button>
      )}
    </section>
  );
}

function errorMessage(error: unknown): string {
  if (!isApiError(error)) return '요청 결과를 확인하지 못했습니다.';
  if (error.status === 401) return '로그인이 필요합니다.';
  if (error.status === 403) return '현재 계정에 지점 정보 권한이 없습니다.';
  if (error.status === 404) return '현재 지점에서 Unit을 찾을 수 없습니다.';
  if (error.status === 409) return '현재 분류가 변경되었습니다.';
  if (error.status === 422)
    return error.code === 'INVALID_REQUEST'
      ? '요청 형식을 확인해 주세요.'
      : '분류 입력 또는 중복 명령을 확인해 주세요.';
  if (error.status === 503) return '저장소를 사용할 수 없습니다. 같은 명령을 재시도해 주세요.';
  return '분류 요청을 처리하지 못했습니다.';
}
